import React, { useState } from 'react';
import { getDailyProfitPivot, DailyProfitPivot } from '../../api/profit';
import { useDateRange } from '../../stores/useDateRangeStore';
import './ExportsTab.css';

interface ExportOption {
  name: string;
  description: string;
  format: string;
}

interface ExportsTabProps {
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const ExportsTab: React.FC<ExportsTabProps> = ({ isLoading, error, onRetry }) => {
  const { dateRange } = useDateRange();
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportOptions: ExportOption[] = [
    {
      name: 'Day/Asset Pivot',
      description: 'A daily profit breakdown pivoted by Asset',
      format: 'CSV',
    },
  ];

  const handleExport = async (optionName: string) => {
    setExportingId(optionName);
    setExportError(null);

    try {
      // Fetch pivot data from API
      const pivotData = await getDailyProfitPivot(dateRange.start_date, dateRange.end_date);

      // Generate CSV content
      const csv = generateCsv(pivotData);

      // Trigger download
      downloadCsv(csv, `profit-pivot-${dateRange.start_date}-to-${dateRange.end_date}.csv`);
    } catch (err) {
      console.error('Error exporting data:', err);
      setExportError(err instanceof Error ? err.message : 'Failed to export data');
    } finally {
      setExportingId(null);
    }
  };

  const generateCsv = (data: DailyProfitPivot[]): string => {
    // Define headers - using product names without -USDC suffix
    const headers = ['Metric Date', 'ADA', 'AVAX', 'BTC', 'ETH', 'LINK', 'SOL', 'SUI', 'XRP', 'Total'];
    
    // Helper function to format date as M/D/YYYY
    const formatDate = (dateStr: string): string => {
      const [year, month, day] = dateStr.split('-');
      return `${parseInt(month)}/${parseInt(day)}/${year}`;
    };
    
    // Create CSV rows
    const rows = data.map(row => [
      formatDate(row.last_fill_date),
      row.ADA_USDC.toFixed(2),
      row.AVAX_USDC.toFixed(2),
      row.BTC_USDC.toFixed(2),
      row.ETH_USDC.toFixed(2),
      row.LINK_USDC.toFixed(2),
      row.SOL_USDC.toFixed(2),
      row.SUI_USDC.toFixed(2),
      row.XRP_USDC.toFixed(2),
      row.total_profit.toFixed(2),
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');

    return csvContent;
  };

  const downloadCsv = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="exports-loading">
        <div className="exports-loading-spinner" />
        <span>Loading export options...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="exports-error">
        <div className="exports-error-title">Failed to load export options</div>
        <div className="exports-error-message">{error}</div>
        {onRetry && (
          <button className="exports-retry-button" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="exports-tab">
      <div className="exports-table-container">
        <table className="exports-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Format</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {exportOptions.map((option) => (
              <tr key={option.name}>
                <td className="export-name">{option.name}</td>
                <td className="export-description">{option.description}</td>
                <td className="export-format">{option.format}</td>
                <td className="export-action">
                  <button
                    className="export-button"
                    onClick={() => handleExport(option.name)}
                    disabled={exportingId === option.name}
                  >
                    {exportingId === option.name ? 'Exporting...' : 'Export'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {exportError && (
          <div className="export-error-message">
            <span className="error-icon">⚠️</span>
            {exportError}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportsTab;
